import { useContext, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { archiveSectionAPI, copySectionAPI, createSectionAPI, updateSectionAPI } from "../../../utils/api/menu";
import { Input, NoticeBox } from "../../ui";
import { SectionFields } from "../foodMenu/AddSectionModal/AddSectionModal";
import { menuErrorMessage } from "../menuRole";
import { topActionsText } from "./topActionsText";
import { ActionDialog, CopyLinksPreview, DialogFooter, MenuPicker, RestaurantPicker, SectionPicker, bothNames, copyResultMessage, findSection, useCopyPreview, useCopyRestaurants, useDropInvalid, useMenuDetail, useRestaurantMenus } from "./shared";

const emptySection = () => ({ name: { ar: "", en: "" } });
const sectionToForm = (section) => ({ name: { ar: section?.name?.ar || "", en: section?.name?.en || "" } });

// Section → Add / Update / Copy / Archive from the top of the Menu page.
//   add     : Menu → section fields → save
//   update  : Menu → Section → fields (prefilled; items are never touched)
//   copy    : source Restaurant → Menu → Section, destination Restaurant →
//             Menu (section + all items; cross-restaurant links previewed)
//   archive : Menu → Section → confirm (states how many items it holds)
export default function SectionActionDialog({ mode, menus, restaurant, initialMenuId, onClose, onDone }) {
  const { can } = useAccess();
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const isCopy = mode === "copy";
  const currentRestaurantId = restaurant?._id ? String(restaurant._id) : "";
  const isEditable = (id) => menus.some((m) => String(m._id) === String(id) && m.lifecycle !== "archived");
  const [menuId, setMenuId] = useState(mode === "copy" || isEditable(initialMenuId) ? initialMenuId || "" : "");
  const [sectionId, setSectionId] = useState("");
  const [destMenuId, setDestMenuId] = useState(isEditable(initialMenuId) ? initialMenuId : "");
  const [data, setData] = useState(emptySection());
  const [copyName, setCopyName] = useState({ ar: "", en: "" });
  const [srcRestaurantId, setSrcRestaurantId] = useState(currentRestaurantId);
  const [destRestaurantId, setDestRestaurantId] = useState(currentRestaurantId);
  const [additionMap, setAdditionMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const { menu, loading } = useMenuDetail(mode === "add" ? "" : menuId);
  const section = findSection(menu, sectionId);
  const sectionIds = useMemo(() => (menu?.sections || []).map((s) => String(s._id)), [menu]);
  useDropInvalid(sectionId, sectionIds, () => selectSection(""), Boolean(menu));

  function selectSection(id) {
    setSectionId(id);
    setData(sectionToForm(findSection(menu, id)));
    setAdditionMap({});
  }
  const selectMenu = (id) => {
    setMenuId(id);
    setSectionId("");
    setData(emptySection());
    setAdditionMap({});
    setError("");
  };

  // Copy: each side picks its restaurant first; a restaurant change clears
  // that side's menu (and section), so no stale id survives.
  const copyRestaurants = useCopyRestaurants(restaurant, isCopy);
  // Source AND destination restaurant of a copy are both checked (NA-011).
  const { canFor: canCopyFor } = useRestaurantAccess(copyRestaurants.restaurants);
  const srcMenus = useRestaurantMenus(isCopy ? srcRestaurantId : "", { currentRestaurantId, currentMenus: menus, includeArchived: true });
  const destMenus = useRestaurantMenus(isCopy ? destRestaurantId : "", { currentRestaurantId, currentMenus: menus });
  const destEditable = destMenus.menus.filter((m) => m.lifecycle !== "archived");
  const crossRestaurant = isCopy && Boolean(srcRestaurantId && destRestaurantId) && srcRestaurantId !== destRestaurantId;
  const selectSrcRestaurant = (id) => {
    setSrcRestaurantId(id);
    selectMenu("");
  };
  const selectDestRestaurant = (id) => {
    setDestRestaurantId(id);
    setDestMenuId("");
    setAdditionMap({});
    setError("");
  };
  const copyRequest =
    isCopy && section && destMenuId
      ? { source: { restaurantId: srcRestaurantId, menuId, sectionId }, destination: { restaurantId: destRestaurantId, menuId: destMenuId }, ...(Object.keys(additionMap).length ? { additionMap } : {}) }
      : null;
  const copyPreview = useCopyPreview(copyRequest && { type: "section", ...copyRequest }, crossRestaurant);

  const hasName = (value) => Boolean(value?.ar?.trim() || value?.en?.trim());
  const ready = { add: menuId && hasName(data.name), update: section && hasName(data.name), copy: copyRequest && (!crossRestaurant || copyPreview.preview), archive: section }[mode];
  const title = { add: t.addSection, update: t.updateSection, copy: t.copySection, archive: t.archiveSection }[mode];
  const itemCount = section?.items?.length || 0;

  const submit = async () => {
    if (!can("menu.manage", { restaurantId: restaurant?.id })) return;
    if (mode === "copy" && !(canCopyFor("menu.manage", srcRestaurantId) && canCopyFor("menu.manage", destRestaurantId))) {
      setError(menuErrorMessage({ status: 403 }, lang));
      return;
    }
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (mode === "add") {
        const result = await createSectionAPI(menuId, { name: data.name });
        onDone({ menuId, menu: result.menu, message: t.sectionAdded });
      } else if (mode === "update") {
        if (JSON.stringify(sectionToForm(section)) === JSON.stringify(data)) {
          setError(t.nothingChanged);
          return;
        }
        const result = await updateSectionAPI(menuId, sectionId, { name: data.name });
        onDone({ menuId, menu: result.menu, message: t.sectionUpdated });
      } else if (mode === "copy") {
        const result = await copySectionAPI({ ...copyRequest, ...(hasName(copyName) ? { name: copyName } : {}) });
        onDone({ menuId: destMenuId, menu: result.menu, message: copyResultMessage(t, t.sectionCopied(result.itemCount ?? itemCount), result) });
      } else {
        const result = await archiveSectionAPI(menuId, sectionId);
        onDone({ menuId, menu: result.menu, message: t.sectionArchived });
      }
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    } finally {
      setBusy(false);
    }
  };

  const editable = menus.filter((m) => m.lifecycle !== "archived");

  return (
    <ActionDialog
      title={title}
      busy={busy}
      error={error}
      onClose={onClose}
      footer={<DialogFooter submitLabel={{ add: t.save, update: t.save, copy: t.copy, archive: t.archive }[mode]} tone={mode === "archive" ? "danger" : "primary"} onSubmit={submit} onCancel={onClose} busy={busy} disabled={!ready} />}
    >
      {!isCopy && !editable.length && <NoticeBox variant="neutral" size="sm">{t.noMenus}</NoticeBox>}
      {isCopy && <h4 className="menu-action-step">{t.source}</h4>}
      <div className="menu-action-pickers">
        {isCopy && <RestaurantPicker label={t.sourceRestaurant} restaurants={copyRestaurants.restaurants} loading={copyRestaurants.loading} value={srcRestaurantId} onChange={selectSrcRestaurant} disabled={busy} />}
        <MenuPicker label={isCopy ? t.sourceMenu : t.menu} menus={isCopy ? srcMenus.menus : editable} value={menuId} onChange={selectMenu} disabled={busy || (isCopy && srcMenus.loading)} />
        {mode !== "add" && <SectionPicker label={mode === "copy" ? t.sourceSection : t.section_} menu={menu} loading={loading} value={sectionId} onChange={selectSection} disabled={busy} />}
      </div>

      {(mode === "add" || (mode === "update" && section)) && <SectionFields data={data} onChange={setData} />}

      {mode === "copy" && (
        <>
          <h4 className="menu-action-step">{t.destination}</h4>
          <div className="menu-action-pickers">
            <RestaurantPicker label={t.destinationRestaurant} restaurants={copyRestaurants.restaurants} loading={copyRestaurants.loading} value={destRestaurantId} onChange={selectDestRestaurant} disabled={busy} />
            <MenuPicker label={t.destinationMenu} menus={destEditable} value={destMenuId} onChange={setDestMenuId} disabled={busy || destMenus.loading} />
          </div>
          {destRestaurantId && !destMenus.loading && !destEditable.length && <NoticeBox variant="neutral" size="sm">{t.noMenusInRestaurant}</NoticeBox>}
          <div className="menu-action-pickers">
            <Input label={`${t.copyName} (AR)`} value={copyName.ar} placeholder={section?.name?.ar || ""} onChange={(e) => setCopyName((prev) => ({ ...prev, ar: e.target.value }))} />
            <Input label={`${t.copyName} (EN)`} value={copyName.en} placeholder={section?.name?.en || ""} onChange={(e) => setCopyName((prev) => ({ ...prev, en: e.target.value }))} />
          </div>
          <p className="menu-note">
            {t.copyNameHint} {t.copySectionNote}
          </p>
        </>
      )}

      {mode === "copy" && section && (
        <div className="menu-action-summary">
          <div>
            <strong>{bothNames(section.name)}</strong>
            <div className="menu-secondary">{itemCount ? t.sectionHasItems(itemCount) : t.sectionEmpty}</div>
          </div>
        </div>
      )}
      {crossRestaurant && copyRequest && (
        <CopyLinksPreview preview={copyPreview.preview} loading={copyPreview.loading} error={copyPreview.error} disabled={busy} onMap={(sourceId, destId) => setAdditionMap((prev) => ({ ...prev, [sourceId]: destId }))} />
      )}
      {mode === "archive" && section && (
        <NoticeBox variant="danger" size="sm" title={`${bothNames(section.name)} — ${itemCount ? t.sectionHasItems(itemCount) : t.sectionEmpty}`}>
          {t.archiveSectionBody}
        </NoticeBox>
      )}
    </ActionDialog>
  );
}
